/**
 * REGISTRO DE DESPLIEGUE — único punto que documenta y centraliza las variables
 * de entorno que deciden el perfil del PMIS (backend, transporte, identidad,
 * endurecimiento). Pensado como entrada para la AUDITORÍA: todos los switches
 * que cambian el comportamiento de seguridad/datos están listados aquí.
 *
 * ┌─ Switch ───────────┬─ Variable ─────────────┬─ Dónde se APLICA ──────────────┐
 * │ Backend de datos   │ VITE_BACKEND           │ repos.ts (inline, tree-shaking)│
 * │ Transporte SP      │ VITE_SP_TRANSPORT      │ spTransport.ts (inline, idem)  │
 * │ Identidad de datos │ VITE_DATA_AUTH         │ supabaseClient.ts              │
 * │ Scope API de datos │ VITE_DATA_API_SCOPE    │ dataToken.ts                   │
 * │ Capa offline       │ VITE_OFFLINE           │ repos.sharepoint.ts            │
 * │ Endurecimiento     │ VITE_HARDEN_AUTH       │ credenciales.ts                │
 * │ Tenant (etiqueta)  │ VITE_TENANT_ID         │ auth providers                 │
 * └────────────────────┴────────────────────────┴────────────────────────────────┘
 *
 * NOTA DE TREE-SHAKING: `VITE_BACKEND` y `VITE_SP_TRANSPORT` se leen DIRECTO en
 * su punto de selección (no vía este objeto) porque Vite los inlinea como
 * literales y eso permite eliminar los backends/transportes no usados del bundle
 * (aislamiento de los tres artefactos). Este registro los refleja para auditoría
 * pero NO debe ser la vía de selección de esos dos.
 */

export type Backend = "supabase" | "sharepoint" | "dexie";
export type SpTransport = "cookie" | "graph";
export type DataAuth = "anon" | "entra";

function leer(clave: string): string | undefined {
  const v = (import.meta.env as Record<string, string | undefined>)[clave];
  return v && v !== "" ? v : undefined;
}

/** Configuración de despliegue (congelada): la fuente de verdad para auditoría. */
export const DEPLOYMENT = Object.freeze({
  /** Destino de datos. Selección real (tree-shakeable) en repos.ts. */
  backend: (leer("VITE_BACKEND") ?? "dexie") as Backend,
  /** Transporte del backend SharePoint. Selección real en spTransport.ts. */
  spTransport: (leer("VITE_SP_TRANSPORT") ?? "cookie") as SpTransport,
  /** Identidad de la capa de datos: "anon" (piloto) o "entra" (token Entra → RLS). */
  dataAuth: (leer("VITE_DATA_AUTH") === "entra" ? "entra" : "anon") as DataAuth,
  /** Audiencia/scope del token de datos (ej. api://<client-id>/pmis.access). */
  dataApiScope: leer("VITE_DATA_API_SCOPE"),
  /** Capa offline (caché local + outbox) activa. */
  offline: leer("VITE_OFFLINE") === "1",
  /** Build endurecido: sin claves de prueba, fuerza acceso por-usuario. */
  hardened: leer("VITE_HARDEN_AUTH") === "1",
  /** Etiqueta de datos del PMIS (tenantId de negocio, no el tenant de Azure). */
  tenantId: leer("VITE_TENANT_ID") ?? "tecpetrol-cpo13",
});
